/**
 * Orthogonal edge router.
 *
 * One edge in, one polyline out. The polyline always starts on the source port,
 * ends on the target port, leaves and enters perpendicular to the node (so the
 * arrow head meets the box square on), and consists of horizontal and vertical
 * segments only.
 *
 * The work is ordered cheapest-first: a straight shot, then the classic one- and
 * two-bend connectors, then A* over a Hanan grid, then a detour around the
 * outer bounding box that cannot fail. Most edges in a real flowchart never get
 * past the second stage.
 */
import type { FlowDirection, Point, PortSide, Rect } from '../model/types';
import {
  EPSILON,
  countBends,
  countCrossings,
  isOrthogonal,
  normalizePolyline,
  orthogonalize,
  polylineLength,
  quantize,
  segmentIntersectsRect,
  segmentsOf,
} from './polyline';

export interface RoutingObstacle {
  readonly id: string;
  readonly rect: Rect;
}

export interface RoutingOptions {
  /** Clearance kept around obstacles. */
  readonly nodePadding: number;
  /** Straight run leaving and entering a port. */
  readonly stubLength: number;
  /** Quantisation for fallback lanes. */
  readonly gridStep: number;
  /** A* cost per 90-degree turn. */
  readonly bendPenalty: number;
  /** Cost per place a route crosses itself. */
  readonly crossingPenalty: number;
  /** Hard search budget; exceeding it falls back to a coarser route. */
  readonly maxExpansions: number;
}

export interface RouteEndpoint {
  readonly rect: Rect;
  readonly port: PortSide;
}

export interface RouteRequest {
  readonly edgeId: string;
  readonly source: RouteEndpoint;
  readonly target: RouteEndpoint;
  /** Must already exclude the source and target boxes. */
  readonly obstacles: readonly RoutingObstacle[];
  readonly direction: FlowDirection;
  /** Signed lateral nudge that separates parallel edges sharing a channel. */
  readonly laneOffset?: number;
}

export interface RouteResult {
  readonly points: Point[];
  readonly labelPosition: Point | null;
}

export const DEFAULT_ROUTING_OPTIONS: RoutingOptions = {
  nodePadding: 12,
  stubLength: 18,
  gridStep: 8,
  bendPenalty: 22,
  crossingPenalty: 6,
  maxExpansions: 20000,
};

/** Cost of crossing a node. Large enough that any detour wins. */
const BLOCKED_COST = 1e6;
/** Sideways nudge so a label does not sit on top of a vertical connector. */
const LABEL_OFFSET = 8;
/** Tie-break weight nudging routes into the channel the flow direction implies. */
const DIRECTION_BIAS = 0.5;
/** Upper bound on Hanan grid lines per axis. */
const GRID_LINE_CAP = 120;

/** 0 = horizontal, 1 = vertical. Matches the low bit of an A* state id. */
type Axis = 0 | 1;

interface RouteContext {
  readonly sourcePoint: Point;
  readonly targetPoint: Point;
  readonly sourceStub: Point;
  readonly targetStub: Point;
  readonly sourceNormal: Point;
  readonly targetNormal: Point;
  readonly sourceAxis: Axis;
  readonly targetAxis: Axis;
  readonly blockers: readonly Rect[];
  readonly lane: number;
  readonly options: RoutingOptions;
}

interface ScoredRoute {
  readonly points: Point[];
  readonly cost: number;
  readonly hits: number;
}

export function routeEdge(request: RouteRequest, options?: Partial<RoutingOptions>): RouteResult {
  const resolved: RoutingOptions = { ...DEFAULT_ROUTING_OPTIONS, ...options };
  const context = buildContext(request, resolved);
  const points = finalize(computeRoute(request, context), context, request.edgeId);
  return { points, labelPosition: labelPositionFor(points) };
}

/* ------------------------------------------------------------------ *
 * Setup
 * ------------------------------------------------------------------ */

function buildContext(request: RouteRequest, options: RoutingOptions): RouteContext {
  const stub = Math.max(0, options.stubLength);
  const sourcePoint = portAnchor(request.source.rect, request.source.port);
  const targetPoint = portAnchor(request.target.rect, request.target.port);
  const sourceNormal = portNormal(request.source.port);
  const targetNormal = portNormal(request.target.port);
  const sourceStub: Point = {
    x: sourcePoint.x + sourceNormal.x * stub,
    y: sourcePoint.y + sourceNormal.y * stub,
  };
  const targetStub: Point = {
    x: targetPoint.x + targetNormal.x * stub,
    y: targetPoint.y + targetNormal.y * stub,
  };

  const lane =
    typeof request.laneOffset === 'number' && Number.isFinite(request.laneOffset)
      ? request.laneOffset
      : 0;

  return {
    sourcePoint,
    targetPoint,
    sourceStub,
    targetStub,
    sourceNormal,
    targetNormal,
    sourceAxis: portAxis(request.source.port),
    targetAxis: portAxis(request.target.port),
    blockers: collectBlockers(request, options, sourceStub, targetStub, lane),
    lane,
    options,
  };
}

/**
 * Obstacles grow by `nodePadding`; the two endpoint boxes grow by as much as
 * the stub can clear, so a route never hugs the box it just left. A box that
 * swallows one of the stub ends is dropped — with overlapping geometry there is
 * no route at all otherwise.
 *
 * Only obstacles near the two endpoints are kept. Every candidate route lives
 * inside the endpoint union grown by the lane margin, and the search grid is
 * spanned by the obstacles that reach into it, so a box outside that hull
 * cannot be hit. This is what keeps whole-graph routing linear rather than
 * quadratic in the node count.
 */
function collectBlockers(
  request: RouteRequest,
  options: RoutingOptions,
  sourceStub: Point,
  targetStub: Point,
  lane: number,
): Rect[] {
  const padding = Math.max(0, options.nodePadding);
  const endpointPadding = Math.min(padding, Math.max(0, options.stubLength - 1));
  const margin = padding + Math.max(0, options.stubLength) + Math.abs(lane) + 1;
  const region = expandRect(unionRect(request.source.rect, request.target.rect), margin);

  // Raw obstacle rects are compared against the region grown by `padding`,
  // which is equivalent to growing each obstacle and saves an allocation for
  // every box that turns out to be far away.
  const probe = expandRect(region, padding);
  const rects: Rect[] = [];
  let hull = region;
  for (const obstacle of request.obstacles) {
    if (!rectsOverlap(obstacle.rect, probe)) continue;
    const expanded = expandRect(obstacle.rect, padding);
    rects.push(expanded);
    hull = unionRect(hull, expanded);
  }

  // A kept obstacle can stretch the search grid past the region; one more pass
  // picks up whatever now falls inside it. Bounded on purpose — a third pass
  // has never changed the outcome and the cost is paid per edge.
  if (hull.width > region.width + EPSILON || hull.height > region.height + EPSILON) {
    const wider = expandRect(hull, padding);
    for (const obstacle of request.obstacles) {
      if (rectsOverlap(obstacle.rect, probe)) continue;
      if (rectsOverlap(obstacle.rect, wider)) rects.push(expandRect(obstacle.rect, padding));
    }
  }

  rects.push(expandRect(request.source.rect, endpointPadding));
  rects.push(expandRect(request.target.rect, endpointPadding));
  return rects.filter(
    (rect) => !containsStrictly(rect, sourceStub) && !containsStrictly(rect, targetStub),
  );
}

function portAnchor(rect: Rect, side: PortSide): Point {
  switch (side) {
    case 'top':
      return { x: rect.x + rect.width / 2, y: rect.y };
    case 'bottom':
      return { x: rect.x + rect.width / 2, y: rect.y + rect.height };
    case 'left':
      return { x: rect.x, y: rect.y + rect.height / 2 };
    case 'right':
      return { x: rect.x + rect.width, y: rect.y + rect.height / 2 };
  }
}

function portNormal(side: PortSide): Point {
  switch (side) {
    case 'top':
      return { x: 0, y: -1 };
    case 'bottom':
      return { x: 0, y: 1 };
    case 'left':
      return { x: -1, y: 0 };
    case 'right':
      return { x: 1, y: 0 };
  }
}

function portAxis(side: PortSide): Axis {
  return side === 'left' || side === 'right' ? 0 : 1;
}

/* ------------------------------------------------------------------ *
 * Route selection
 * ------------------------------------------------------------------ */

function computeRoute(request: RouteRequest, context: RouteContext): readonly Point[] {
  const { sourceStub: a, targetStub: b } = context;
  const pool: ScoredRoute[] = [];

  // 1. Straight shot between the stub ends.
  if (quantize(a.x) === quantize(b.x) || quantize(a.y) === quantize(b.y)) {
    const direct = score([], context);
    if (direct.hits === 0) return direct.points;
    pool.push(direct);
  }

  // 2. The classic one- and two-bend connectors, plus lanes beyond either box.
  const preferVertical = request.direction === 'top-to-bottom';
  for (const x of verticalLanes(request, context)) {
    pool.push(
      score(
        [
          { x, y: a.y },
          { x, y: b.y },
        ],
        context,
        preferVertical ? DIRECTION_BIAS : 0,
      ),
    );
  }
  for (const y of horizontalLanes(request, context)) {
    pool.push(
      score(
        [
          { x: a.x, y },
          { x: b.x, y },
        ],
        context,
        preferVertical ? 0 : DIRECTION_BIAS,
      ),
    );
  }

  let best = pickBest(pool);
  if (best.hits === 0) return best.points;

  // 3. A* over a Hanan grid built from the obstacle edges.
  if (context.options.maxExpansions > 0) {
    const searched = searchGrid(context);
    if (searched) {
      const candidate = score(searched, context);
      if (candidate.hits === 0) return candidate.points;
      pool.push(candidate);
    }
  }

  // 4. Detour around the outer bounding box. Always available, always orthogonal.
  pool.push(score(fallbackMiddle(request, context), context));

  best = pickBest(pool);
  return best.points;
}

function pickBest(pool: readonly ScoredRoute[]): ScoredRoute {
  let best = pool[0];
  for (const candidate of pool) {
    if (candidate.cost + EPSILON < best.cost) best = candidate;
  }
  return best;
}

/**
 * Wraps a middle section in the two stubs, normalizes it and prices it.
 * Obstacle hits dominate; bends, self-crossings and the flow-direction bias
 * only decide between routes that are all legal.
 */
function score(middle: readonly Point[], context: RouteContext, bias = 0): ScoredRoute {
  const points = normalizePolyline([
    context.sourcePoint,
    context.sourceStub,
    ...middle,
    context.targetStub,
    context.targetPoint,
  ]);
  const extent = boundsOf(points);
  let hits = 0;
  for (const rect of context.blockers) {
    if (!boundsOverlapRect(extent, rect)) continue;
    if (polylineHitsRect(points, rect)) hits += 1;
  }
  const selfCrossings = countCrossings(points, points) / 2;
  const cost =
    polylineLength(points) +
    context.options.bendPenalty * countBends(points) +
    context.options.crossingPenalty * selfCrossings +
    context.options.bendPenalty * 2 * countReversals(points, context) +
    hits * BLOCKED_COST +
    bias;
  return { points, cost, hits };
}

/**
 * How many ends leave against their port normal. Normalizing can swallow a stub
 * when the route doubles straight back over it; the result is still legal, but
 * an arrow that leaves through the side of its own box reads badly, so it is
 * priced well above a bend and well below an obstacle.
 */
function countReversals(points: readonly Point[], context: RouteContext): number {
  if (points.length < 2) return 0;
  const last = points.length - 1;
  let reversals = 0;
  const outDx = points[1].x - points[0].x;
  const outDy = points[1].y - points[0].y;
  if (outDx * context.sourceNormal.x + outDy * context.sourceNormal.y < 0) reversals += 1;
  const inDx = points[last - 1].x - points[last].x;
  const inDy = points[last - 1].y - points[last].y;
  if (inDx * context.targetNormal.x + inDy * context.targetNormal.y < 0) reversals += 1;
  return reversals;
}

function polylineHitsRect(points: readonly Point[], rect: Rect): boolean {
  for (let i = 1; i < points.length; i += 1) {
    if (segmentIntersectsRect(points[i - 1], points[i], rect)) return true;
  }
  return false;
}

/**
 * x positions for the horizontal-vertical-horizontal family.
 *
 * Every two-bend connector between the same pair of stub ends has the same
 * length and the same bend count, so this order is what actually decides the
 * winner. The channel half way between the boxes comes first: it is the
 * conventional flowchart shape, and it is the one the lane offset can move,
 * which is how parallel edges end up in separate lanes.
 */
function verticalLanes(request: RouteRequest, context: RouteContext): number[] {
  const { sourceStub: a, targetStub: b, options, lane } = context;
  const union = unionRect(request.source.rect, request.target.rect);
  const outer = options.nodePadding + Math.max(0, options.stubLength);
  const shift = clamp(lane, -outer / 2, outer / 2);
  return dedupe([
    (a.x + b.x) / 2 + lane,
    a.x,
    b.x,
    union.x - outer + shift,
    union.x + union.width + outer + shift,
  ]);
}

/** y positions for the vertical-horizontal-vertical family, mid channel first. */
function horizontalLanes(request: RouteRequest, context: RouteContext): number[] {
  const { sourceStub: a, targetStub: b, options, lane } = context;
  const union = unionRect(request.source.rect, request.target.rect);
  const outer = options.nodePadding + Math.max(0, options.stubLength);
  const shift = clamp(lane, -outer / 2, outer / 2);
  return dedupe([
    (a.y + b.y) / 2 + lane,
    a.y,
    b.y,
    union.y - outer + shift,
    union.y + union.height + outer + shift,
  ]);
}

/* ------------------------------------------------------------------ *
 * A* over a Hanan grid
 * ------------------------------------------------------------------ */

function searchGrid(context: RouteContext): Point[] | null {
  const { sourceStub: a, targetStub: b, blockers, options } = context;

  const extraX: number[] = [context.sourcePoint.x, context.targetPoint.x];
  const extraY: number[] = [context.sourcePoint.y, context.targetPoint.y];
  for (const rect of blockers) {
    extraX.push(rect.x, rect.x + rect.width);
    extraY.push(rect.y, rect.y + rect.height);
  }
  const xs = axisLines([a.x, b.x], extraX);
  const ys = axisLines([a.y, b.y], extraY);
  const nx = xs.length;
  const ny = ys.length;
  if (nx < 2 && ny < 2) return null;

  const startX = xs.indexOf(quantize(a.x));
  const startY = ys.indexOf(quantize(a.y));
  const goalX = xs.indexOf(quantize(b.x));
  const goalY = ys.indexOf(quantize(b.y));
  if (startX < 0 || startY < 0 || goalX < 0 || goalY < 0) return null;

  const stateCount = nx * ny * 2;
  const gScore = new Float64Array(stateCount).fill(Infinity);
  const cameFrom = new Int32Array(stateCount).fill(-1);
  const closed = new Uint8Array(stateCount);
  // -1 unknown, 0 free, 1 blocked. Each grid segment is tested at most once.
  const horizontalFree = new Int8Array(Math.max(0, (nx - 1) * ny)).fill(-1);
  const verticalFree = new Int8Array(Math.max(0, nx * (ny - 1))).fill(-1);

  const isFree = (cache: Int8Array, index: number, from: Point, to: Point): boolean => {
    const cached = cache[index];
    if (cached >= 0) return cached === 0;
    let blocked = 0;
    for (const rect of blockers) {
      if (segmentIntersectsRect(from, to, rect)) {
        blocked = 1;
        break;
      }
    }
    cache[index] = blocked;
    return blocked === 0;
  };

  const heap = new MinHeap();
  const start = (startY * nx + startX) * 2 + context.sourceAxis;
  gScore[start] = 0;
  heap.push(heuristic(xs[startX], ys[startY], b), start);

  let expansions = 0;
  let goal = -1;
  while (heap.size > 0) {
    const current = heap.pop();
    if (closed[current] === 1) continue;
    closed[current] = 1;
    expansions += 1;
    if (expansions > options.maxExpansions) return null;

    const cell = current >> 1;
    const xi = cell % nx;
    const yi = (cell - xi) / nx;
    if (xi === goalX && yi === goalY) {
      goal = current;
      break;
    }

    const axis: Axis = (current & 1) as Axis;
    const base = gScore[current];
    const relax = (nextX: number, nextY: number, nextAxis: Axis, step: number): void => {
      const turn = nextAxis === axis ? 0 : options.bendPenalty;
      const atGoal = nextX === goalX && nextY === goalY;
      const arrival = atGoal && nextAxis !== context.targetAxis ? options.bendPenalty : 0;
      const tentative = base + step + turn + arrival;
      const next = (nextY * nx + nextX) * 2 + nextAxis;
      if (closed[next] === 1 || tentative >= gScore[next]) return;
      gScore[next] = tentative;
      cameFrom[next] = current;
      heap.push(tentative + heuristic(xs[nextX], ys[nextY], b), next);
    };

    if (xi > 0) {
      const from = { x: xs[xi - 1], y: ys[yi] };
      const to = { x: xs[xi], y: ys[yi] };
      if (isFree(horizontalFree, yi * (nx - 1) + (xi - 1), from, to)) {
        relax(xi - 1, yi, 0, to.x - from.x);
      }
    }
    if (xi < nx - 1) {
      const from = { x: xs[xi], y: ys[yi] };
      const to = { x: xs[xi + 1], y: ys[yi] };
      if (isFree(horizontalFree, yi * (nx - 1) + xi, from, to)) {
        relax(xi + 1, yi, 0, to.x - from.x);
      }
    }
    if (yi > 0) {
      const from = { x: xs[xi], y: ys[yi - 1] };
      const to = { x: xs[xi], y: ys[yi] };
      if (isFree(verticalFree, (yi - 1) * nx + xi, from, to)) {
        relax(xi, yi - 1, 1, to.y - from.y);
      }
    }
    if (yi < ny - 1) {
      const from = { x: xs[xi], y: ys[yi] };
      const to = { x: xs[xi], y: ys[yi + 1] };
      if (isFree(verticalFree, yi * nx + xi, from, to)) {
        relax(xi, yi + 1, 1, to.y - from.y);
      }
    }
  }

  if (goal < 0) return null;

  const path: Point[] = [];
  for (let state = goal; state >= 0; state = cameFrom[state]) {
    const cell = state >> 1;
    const xi = cell % nx;
    const yi = (cell - xi) / nx;
    path.push({ x: xs[xi], y: ys[yi] });
  }
  path.reverse();
  return path;
}

function heuristic(x: number, y: number, goal: Point): number {
  return Math.abs(x - goal.x) + Math.abs(y - goal.y);
}

/**
 * Grid lines for one axis: the required values, the obstacle edges, and the
 * midpoint between every adjacent pair so a route can thread a gap. Subsampled
 * when the obstacle count would blow the cap; the required values always
 * survive, otherwise the endpoints would be unreachable.
 */
function axisLines(required: readonly number[], extra: readonly number[]): number[] {
  const base = dedupeSorted([...required, ...extra].map(quantize));
  const withMidpoints: number[] = [];
  for (let i = 0; i < base.length; i += 1) {
    withMidpoints.push(base[i]);
    if (i + 1 < base.length) withMidpoints.push(quantize((base[i] + base[i + 1]) / 2));
  }
  const merged = dedupeSorted(withMidpoints);
  if (merged.length <= GRID_LINE_CAP) return merged;

  const keep = new Set(required.map(quantize));
  const pinned = merged.filter((value) => keep.has(value));
  const rest = merged.filter((value) => !keep.has(value));
  const slots = Math.max(0, GRID_LINE_CAP - pinned.length);
  const sampled = [...pinned];
  if (slots > 0 && rest.length > 0) {
    const stride = rest.length / slots;
    for (let i = 0; i < slots; i += 1) sampled.push(rest[Math.floor(i * stride)]);
  }
  return dedupeSorted(sampled);
}

/** Binary min-heap over (priority, state) pairs. */
class MinHeap {
  private readonly priorities: number[] = [];
  private readonly states: number[] = [];

  get size(): number {
    return this.states.length;
  }

  push(priority: number, state: number): void {
    this.priorities.push(priority);
    this.states.push(state);
    let index = this.states.length - 1;
    while (index > 0) {
      const parent = (index - 1) >> 1;
      if (this.priorities[parent] <= this.priorities[index]) break;
      this.swap(parent, index);
      index = parent;
    }
  }

  /** Returns -1 when empty; callers guard on `size` first. */
  pop(): number {
    const count = this.states.length;
    if (count === 0) return -1;
    const top = this.states[0];
    const lastPriority = this.priorities[count - 1];
    const lastState = this.states[count - 1];
    this.priorities.length = count - 1;
    this.states.length = count - 1;
    if (count > 1) {
      this.priorities[0] = lastPriority;
      this.states[0] = lastState;
      let index = 0;
      for (;;) {
        const left = index * 2 + 1;
        const right = left + 1;
        let smallest = index;
        if (left < this.states.length && this.priorities[left] < this.priorities[smallest]) {
          smallest = left;
        }
        if (right < this.states.length && this.priorities[right] < this.priorities[smallest]) {
          smallest = right;
        }
        if (smallest === index) break;
        this.swap(smallest, index);
        index = smallest;
      }
    }
    return top;
  }

  private swap(i: number, j: number): void {
    const priority = this.priorities[i];
    this.priorities[i] = this.priorities[j];
    this.priorities[j] = priority;
    const state = this.states[i];
    this.states[i] = this.states[j];
    this.states[j] = state;
  }
}

/* ------------------------------------------------------------------ *
 * Fallback: around the outside of everything
 * ------------------------------------------------------------------ */

interface Bounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

function fallbackMiddle(request: RouteRequest, context: RouteContext): Point[] {
  const outer = outerBounds(request, context);
  const from = extendToBounds(context.sourceStub, context.sourceNormal, outer);
  const to = extendToBounds(context.targetStub, context.targetNormal, outer);
  return [from, ...perimeterCorners(outer, from, to), to];
}

function outerBounds(request: RouteRequest, context: RouteContext): Bounds {
  const rects = [request.source.rect, request.target.rect, ...request.obstacles.map((o) => o.rect)];
  let minX = Math.min(context.sourceStub.x, context.targetStub.x);
  let minY = Math.min(context.sourceStub.y, context.targetStub.y);
  let maxX = Math.max(context.sourceStub.x, context.targetStub.x);
  let maxY = Math.max(context.sourceStub.y, context.targetStub.y);
  for (const rect of rects) {
    minX = Math.min(minX, rect.x);
    minY = Math.min(minY, rect.y);
    maxX = Math.max(maxX, rect.x + rect.width);
    maxY = Math.max(maxY, rect.y + rect.height);
  }
  const margin =
    context.options.nodePadding +
    Math.max(0, context.options.stubLength) +
    Math.abs(context.lane) +
    1;
  const step = context.options.gridStep > 0 ? context.options.gridStep : 0;
  return {
    minX: snapOutward(minX - margin, step, -1),
    minY: snapOutward(minY - margin, step, -1),
    maxX: snapOutward(maxX + margin, step, 1),
    maxY: snapOutward(maxY + margin, step, 1),
  };
}

function snapOutward(value: number, step: number, sign: -1 | 1): number {
  if (step <= 0) return value;
  return sign < 0 ? Math.floor(value / step) * step : Math.ceil(value / step) * step;
}

/** Runs straight out along the port normal until it meets the outer frame. */
function extendToBounds(point: Point, normal: Point, outer: Bounds): Point {
  if (normal.x < 0) return { x: outer.minX, y: point.y };
  if (normal.x > 0) return { x: outer.maxX, y: point.y };
  if (normal.y < 0) return { x: point.x, y: outer.minY };
  return { x: point.x, y: outer.maxY };
}

/**
 * Corners passed while walking the outer frame from `from` to `to` the short
 * way round. Both points lie on the frame, so the walk is orthogonal by
 * construction and stays clear of every box.
 */
function perimeterCorners(outer: Bounds, from: Point, to: Point): Point[] {
  const width = outer.maxX - outer.minX;
  const height = outer.maxY - outer.minY;
  const total = 2 * (width + height);
  if (total <= EPSILON) return [];

  const start = perimeterParam(outer, from, width, height);
  const end = perimeterParam(outer, to, width, height);
  const clockwise = (end - start + total) % total;
  const forward = clockwise <= total - clockwise;
  const span = forward ? clockwise : total - clockwise;

  const corners: Array<{ point: Point; at: number }> = [
    { point: { x: outer.minX, y: outer.minY }, at: 0 },
    { point: { x: outer.maxX, y: outer.minY }, at: width },
    { point: { x: outer.maxX, y: outer.maxY }, at: width + height },
    { point: { x: outer.minX, y: outer.maxY }, at: 2 * width + height },
  ];

  const passed: Array<{ point: Point; distance: number }> = [];
  for (const corner of corners) {
    const ahead = (corner.at - start + total) % total;
    const distance = forward ? ahead : (total - ahead) % total;
    if (distance > EPSILON && distance < span - EPSILON)
      passed.push({ point: corner.point, distance });
  }
  passed.sort((a, b) => a.distance - b.distance);
  return passed.map((entry) => entry.point);
}

/** Distance from the top-left corner, measured clockwise. */
function perimeterParam(outer: Bounds, point: Point, width: number, height: number): number {
  if (Math.abs(point.y - outer.minY) <= EPSILON) return clamp(point.x - outer.minX, 0, width);
  if (Math.abs(point.x - outer.maxX) <= EPSILON)
    return width + clamp(point.y - outer.minY, 0, height);
  if (Math.abs(point.y - outer.maxY) <= EPSILON) {
    return width + height + clamp(outer.maxX - point.x, 0, width);
  }
  return 2 * width + height + clamp(outer.maxY - point.y, 0, height);
}

/* ------------------------------------------------------------------ *
 * Output
 * ------------------------------------------------------------------ */

function finalize(points: readonly Point[], context: RouteContext, edgeId: string): Point[] {
  let result = normalizePolyline(orthogonalize(points));

  // Coincident ports collapse the whole polyline to a single point. Emit the
  // source stub instead so downstream code always has a segment to draw.
  if (result.length < 2) {
    const stub = Math.max(1, context.options.stubLength);
    result = normalizePolyline([
      context.sourcePoint,
      {
        x: context.sourcePoint.x + context.sourceNormal.x * stub,
        y: context.sourcePoint.y + context.sourceNormal.y * stub,
      },
    ]);
  }

  if (!isOrthogonal(result)) {
    if (import.meta.env.DEV) {
      throw new Error(`routeEdge produced a non-orthogonal polyline for edge "${edgeId}"`);
    }
    result = normalizePolyline(orthogonalize(result));
  }
  return result;
}

function labelPositionFor(points: readonly Point[]): Point | null {
  if (points.length < 2) return null;
  let best: { a: Point; b: Point; axis: 'h' | 'v' } | null = null;
  let bestLength = 0;
  for (const segment of segmentsOf(points)) {
    const length = Math.hypot(segment.b.x - segment.a.x, segment.b.y - segment.a.y);
    if (length > bestLength + EPSILON) {
      bestLength = length;
      best = segment;
    }
  }
  if (!best || bestLength <= EPSILON) return null;
  const mid = { x: (best.a.x + best.b.x) / 2, y: (best.a.y + best.b.y) / 2 };
  return best.axis === 'v' ? { x: mid.x + LABEL_OFFSET, y: mid.y } : mid;
}

/* ------------------------------------------------------------------ *
 * Small helpers
 * ------------------------------------------------------------------ */

function expandRect(rect: Rect, padding: number): Rect {
  return {
    x: rect.x - padding,
    y: rect.y - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  };
}

function unionRect(a: Rect, b: Rect): Rect {
  const minX = Math.min(a.x, b.x);
  const minY = Math.min(a.y, b.y);
  const maxX = Math.max(a.x + a.width, b.x + b.width);
  const maxY = Math.max(a.y + a.height, b.y + b.height);
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function rectsOverlap(a: Rect, b: Rect): boolean {
  return (
    a.x <= b.x + b.width && b.x <= a.x + a.width && a.y <= b.y + b.height && b.y <= a.y + a.height
  );
}

function boundsOf(points: readonly Point[]): Bounds {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }
  return { minX, minY, maxX, maxY };
}

function boundsOverlapRect(bounds: Bounds, rect: Rect): boolean {
  return (
    bounds.minX <= rect.x + rect.width &&
    rect.x <= bounds.maxX &&
    bounds.minY <= rect.y + rect.height &&
    rect.y <= bounds.maxY
  );
}

function containsStrictly(rect: Rect, point: Point): boolean {
  return (
    point.x > rect.x + EPSILON &&
    point.x < rect.x + rect.width - EPSILON &&
    point.y > rect.y + EPSILON &&
    point.y < rect.y + rect.height - EPSILON
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function dedupe(values: readonly number[]): number[] {
  const seen = new Set<number>();
  const out: number[] = [];
  for (const value of values) {
    const key = quantize(value);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

function dedupeSorted(values: readonly number[]): number[] {
  const sorted = [...values].sort((a, b) => a - b);
  const out: number[] = [];
  for (const value of sorted) {
    if (out.length === 0 || out[out.length - 1] !== value) out.push(value);
  }
  return out;
}
